// Чистая логика над линией редактора маршрутов (вершины + леги): выбор лега
// тапом, "туда-обратно", проверка замкнутости. Без карты и DOM - проецирование
// передаётся функцией project, поэтому всё тестируется в node.
import type { Coordinate } from "./reverseRoute";

export type { Coordinate };

export interface LegLike {
  geometry: Coordinate[];
}

const distToSegmentPx = (p: [number, number], a: [number, number], b: [number, number]): number => {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const lenSq = dx * dx + dy * dy;
  const t = lenSq === 0 ? 0 : Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / lenSq));
  return Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy));
};

// Индекс лега, ближайшего к точке тапа (в пикселях экрана), если он ближе
// maxPx; иначе null. project переводит [lng, lat] в экранные [x, y].
export const findNearestLeg = (
  legs: LegLike[],
  tapPx: [number, number],
  project: (c: Coordinate) => [number, number],
  maxPx: number
): number | null => {
  let best: { index: number; dist: number } | null = null;
  legs.forEach((leg, index) => {
    const pts = leg.geometry.map(project);
    for (let i = 1; i < pts.length; i++) {
      const dist = distToSegmentPx(tapPx, pts[i - 1], pts[i]);
      if (dist <= maxPx && (!best || dist < best.dist)) best = { index, dist };
    }
  });
  return best ? (best as { index: number; dist: number }).index : null;
};

const sameCoordinate = (a: Coordinate, b: Coordinate) => a[0] === b[0] && a[1] === b[1];

// Линия замкнута, если вершин не меньше трёх и финиш совпадает со стартом.
export const isClosedLine = (vertices: Coordinate[]): boolean =>
  vertices.length >= 3 && sameCoordinate(vertices[0], vertices[vertices.length - 1]);

// Середина лега по длине (в плоских градусах - для выбора места ручки
// достаточно): точка, делящая геометрию лега пополам.
export const legMidpoint = (geometry: Coordinate[]): Coordinate => {
  if (geometry.length === 0) return [0, 0];
  if (geometry.length === 1) return geometry[0];
  const lengths: number[] = [];
  let total = 0;
  for (let i = 1; i < geometry.length; i++) {
    const len = Math.hypot(geometry[i][0] - geometry[i - 1][0], geometry[i][1] - geometry[i - 1][1]);
    lengths.push(len);
    total += len;
  }
  if (total === 0) return geometry[0];
  let remaining = total / 2;
  for (let i = 0; i < lengths.length; i++) {
    if (remaining <= lengths[i]) {
      const t = lengths[i] === 0 ? 0 : remaining / lengths[i];
      return [
        geometry[i][0] + (geometry[i + 1][0] - geometry[i][0]) * t,
        geometry[i][1] + (geometry[i + 1][1] - geometry[i][1]) * t,
      ];
    }
    remaining -= lengths[i];
  }
  return geometry[geometry.length - 1];
};

export interface SplittableLeg extends LegLike {
  routed: boolean;
  status: "straight" | "routed" | "fallback";
}

// Новая вершина посередине лега (перетаскивание отрезка): лег legIndex
// делится на два прямых между его концами и новой вершиной; routed
// наследуется (по тропам перекладываются после отпускания). Вход не
// мутируется.
export const splitLegAt = <L extends SplittableLeg>(
  vertices: Coordinate[],
  legs: L[],
  legIndex: number,
  at: Coordinate
): { vertices: Coordinate[]; legs: L[] } => {
  const leg = legs[legIndex];
  if (!leg || legIndex + 1 >= vertices.length) return { vertices: vertices.slice(), legs: legs.slice() };
  const nextVertices = vertices.slice();
  nextVertices.splice(legIndex + 1, 0, [at[0], at[1]]);
  const first = { ...leg, geometry: [nextVertices[legIndex], nextVertices[legIndex + 1]], status: "straight" } as L;
  const second = { ...leg, geometry: [nextVertices[legIndex + 1], nextVertices[legIndex + 2]], status: "straight" } as L;
  const nextLegs = legs.slice();
  nextLegs.splice(legIndex, 1, first, second);
  return { vertices: nextVertices, legs: nextLegs };
};
